import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Group, Layer, Stage } from 'react-konva'
import { useRoute } from 'wouter'

import type { FlattenedDocumentPage } from '@/lib/api/client'
import type { TemplateAttribute } from '@/services/hooks/template_attributes'
import { toRenderableKonvaAnnotation } from './legacyAnnotationMappers'
import KonvaAnnotationNode from './KonvaAnnotationNode'
import KonvaAnnotationInteractionBoundsMirror from './KonvaAnnotationInteractionBoundsMirror'
import KonvaAnnotationColorToolbar from './KonvaAnnotationColorToolbar'
import {
  getSelectedAnnotationColor,
  isColorEditableKonvaAnnotation,
} from './annotationMetadata'
import type { KonvaAnnotationTemplate } from './annotationSchema'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import { cn } from '@/utils'
import { useDocumentState } from '@/state'
import { getAbsoluteYFromSubPage } from './utils'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { HARDWARE_SPECIFICS_MENU_ITEMS } from './menus'
import { Input } from '@/components/ui/input'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import type { KonvaEventObject } from 'konva/lib/Node'
import {
  getDocumentAnnotationBounds,
  getArrowTextLayout,
  getBubbleLayout,
  getLineVector,
  getLineResizeHandleGeometry,
  isLineBasedAnnotation,
  type DocumentAnnotationBounds,
  type LineResizeHandleGeometry,
} from './annotationGeometry'

type KonvaAnnotationsLayerProps = {
  annotationLayerActions: KonvaAnnotationLayerActions
  containerRef: RefObject<HTMLDivElement | null>
  documentPage: FlattenedDocumentPage
  floatingOverlayElement?: HTMLDivElement | null
  isInteractive: boolean
  multiSelectedElementIds?: string[]
  selectedElementId?: string | null
  stackZIndex?: number
  templateAttributes: TemplateAttribute<any>[]
  onAnnotationDragStateChange?: (isDragging: boolean) => void
  onAnnotationDragEnd: ({
    annotation,
    position,
    template,
  }: {
    annotation: KonvaAnnotationTemplate
    position: { x: number; y: number }
    template: TemplateAttribute<any>
  }) => void | Promise<void>
  onAnnotationResizeEnd: ({
    annotation,
    template,
  }: {
    annotation: KonvaAnnotationTemplate
    template: TemplateAttribute<any>
  }) => void | Promise<void>
  onAnnotationColorChange: ({
    annotation,
    color,
    template,
  }: {
    annotation: KonvaAnnotationTemplate
    color: string
    template: TemplateAttribute<any>
  }) => void | Promise<void>
  onAnnotationTextChange: ({
    text,
    template,
  }: {
    text: string
    template: TemplateAttribute<any>
  }) => void | Promise<void>
  onAnnotationSelect: (template: TemplateAttribute<any>) => void
  onClearSelection: () => void
}

type KonvaAnnotationLayerActions = {
  bringForward: (templateId: string) => void
  bringToFront: (templateId: string) => void
  isLoading: boolean
  sendBackward: (templateId: string) => void
  sendToBack: (templateId: string) => void
}

type KonvaAnnotationLayerItemProps = {
  annotation: KonvaAnnotationTemplate
  documentPage: FlattenedDocumentPage
  dragOffset?: { x: number; y: number }
  isInteractive: boolean
  isMultiSelected: boolean
  isSelected: boolean
  lineResizePoints?: number[] | null
  renderArrowText?: boolean
  template: TemplateAttribute<any>
  onAnnotationDragEnd: ({
    annotation,
    position,
    template,
  }: {
    annotation: KonvaAnnotationTemplate
    position: { x: number; y: number }
    template: TemplateAttribute<any>
  }) => void | Promise<void>
  onAnnotationDragMove: ({
    annotation,
    position,
    templateId,
  }: {
    annotation: KonvaAnnotationTemplate
    position: { x: number; y: number }
    templateId: string
  }) => void
  onAnnotationDragStart: (templateId: string) => void
  onAnnotationSelect: (template: TemplateAttribute<any>) => void
  onMultiSelectPointerDown: (
    event: MouseEvent | TouchEvent,
    template: TemplateAttribute<any>,
  ) => void
  onMultiSelectPointerUp: (event: MouseEvent | TouchEvent) => void
  onTooltipHide: (templateId: string) => void
  onContextMenu: ({
    event,
    template,
  }: {
    event: KonvaEventObject<MouseEvent>
    template: TemplateAttribute<any>
  }) => void
  onTooltipShow: ({
    annotation,
    templateId,
  }: {
    annotation: KonvaAnnotationTemplate
    templateId: string
  }) => void
  onNodeRef: (templateId: string, node: any | null) => void
}

type KonvaAnnotationInteractionProxyProps = {
  annotation: KonvaAnnotationTemplate
  bounds: DocumentAnnotationBounds
  isInteractive: boolean
  showArrowText: boolean
  subPageIds: string[]
  template: TemplateAttribute<any>
  templateId: string
  onAnnotationSelect: (template: TemplateAttribute<any>) => void
  onAnnotationTextEditRequest: (templateId: string) => void
  onContextMenu: ({
    clientX,
    clientY,
    template,
  }: {
    clientX: number
    clientY: number
    template: TemplateAttribute<any>
  }) => void
  onMultiSelectPointerDown: (
    event: MouseEvent | TouchEvent | React.PointerEvent<Element>,
    template: TemplateAttribute<any>,
  ) => void
  onMultiSelectPointerUp: (
    event: MouseEvent | TouchEvent | React.PointerEvent<Element>,
  ) => void
  onTooltipHide: (templateId: string) => void
  onTooltipShow: ({
    annotation,
    templateId,
  }: {
    annotation: KonvaAnnotationTemplate
    templateId: string
  }) => void
}

type RenderableAnnotationItem = {
  annotation: KonvaAnnotationTemplate
  bounds: DocumentAnnotationBounds
  position: { x: number; y: number }
  template: TemplateAttribute<any>
  templateId: string
}

type AnnotationStageCluster = {
  bounds: DocumentAnnotationBounds[]
  id: string
  items: RenderableAnnotationItem[]
  rect: DocumentAnnotationBounds
}

type LineResizeDraft = {
  points: number[]
  templateId: string
} | null

type ActiveLineResize = {
  annotation: KonvaAnnotationTemplate
  handle: LineResizeHandleGeometry
  position: { x: number; y: number }
  template: TemplateAttribute<any>
  templateId: string
}

type KeyboardNudgeSettlingState = {
  delta: { x: number; y: number }
  startPosition: { x: number; y: number }
  templateId: string
} | null

type KeyboardNudgePreviewState = NonNullable<KeyboardNudgeSettlingState>

const MIN_HORIZONTAL_STAGE_PADDING = 400
const MIN_VERTICAL_STAGE_PADDING = 180
const IDLE_STAGE_PADDING = 12
const LINE_ANNOTATION_PROXY_HIT_WIDTH = 18
const CONTEXT_MENU_WIDTH = 192
const TOOLTIP_SHOW_DELAY_MS = 1000
const TOOLTIP_VERTICAL_OFFSET = 8
const PRINT_KONVA_PIXEL_RATIO = 300 / 72 // 300 DPI, the correct ratio for standard, high-quality printers
const MAX_SCREEN_KONVA_PIXEL_RATIO = 6
const DEFAULT_ANNOTATION_Z = 1000

const getAnnotationZ = (annotation: KonvaAnnotationTemplate) =>
  annotation.geometry.z ?? DEFAULT_ANNOTATION_Z

const getAnnotationCanvasZ = (annotation: KonvaAnnotationTemplate) =>
  getAnnotationZ(annotation) * 2

const getAnnotationInteractionZ = (annotation: KonvaAnnotationTemplate) =>
  getAnnotationCanvasZ(annotation)

const shouldClearSelectionFromWrapper = (
  event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
) => event.target === event.currentTarget

const HARDWARE_SPECIFICS_LABELS = new Map(
  HARDWARE_SPECIFICS_MENU_ITEMS.map((item) => [
    item.id,
    item.description ??
      item.id
        .split('_')
        .map((part) =>
          part === 'x' ? 'X' : part[0]?.toUpperCase() + part.slice(1),
        )
        .join(' '),
  ]),
)
const HARDWARE_SPECIFICS_WITHOUT_TOOLTIPS = new Set([
  'checkmark',
  'x',
  'fire_hazard',
  'warning',
])

const getHardwareSpecificAnnotationLabel = (
  annotation: KonvaAnnotationTemplate,
) => {
  if (annotation.kind !== 'icon') return null

  const annotationType = annotation.iconName ?? annotation.legacyType
  if (!annotationType) return null
  if (HARDWARE_SPECIFICS_WITHOUT_TOOLTIPS.has(annotationType)) return null

  return HARDWARE_SPECIFICS_LABELS.get(annotationType) ?? null
}

type KonvaAnnotationTooltipState = {
  bounds: DocumentAnnotationBounds
  label: string
  templateId: string
} | null

type KonvaAnnotationTextEditorState = {
  bounds: {
    height: number
    left: number
    top: number
    width: number
  }
  maxLength?: number
  template: TemplateAttribute<any>
  text: string
} | null

type KonvaAnnotationContextMenuState = {
  left: number
  template: TemplateAttribute<any>
  top: number
} | null

const getLocalPointFromClientPoint = ({
  clientX,
  clientY,
  element,
}: {
  clientX: number
  clientY: number
  element: HTMLElement
}) => {
  const bounds = element.getBoundingClientRect()
  const scaleX =
    bounds.width > 0 && element.offsetWidth > 0
      ? bounds.width / element.offsetWidth
      : 1
  const scaleY =
    bounds.height > 0 && element.offsetHeight > 0
      ? bounds.height / element.offsetHeight
      : 1

  return {
    x: (clientX - bounds.left) / scaleX,
    y: (clientY - bounds.top) / scaleY,
  }
}

const rotatePoint = ({
  angle,
  point,
}: {
  angle: number
  point: { x: number; y: number }
}) => {
  if (!angle) return point

  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  }
}

const snapVectorToAngles = (vx: number, vy: number) => {
  const magnitude = Math.hypot(vx, vy)
  if (magnitude === 0) return { x: 0, y: 0 }

  const theta = Math.atan2(vy, vx)
  let degrees = Math.abs(theta * (180 / Math.PI)) % 180
  if (degrees > 90) degrees = 180 - degrees

  const snapAngles = [0, 15, 30, 45, 60, 75, 90]
  const nearestAngle = snapAngles.reduce((nearest, angle) =>
    Math.abs(degrees - angle) < Math.abs(degrees - nearest) ? angle : nearest,
  )
  const snappedRadians = (nearestAngle * Math.PI) / 180

  return {
    x: Math.cos(snappedRadians) * magnitude * (Math.sign(vx) || 1),
    y: Math.sin(snappedRadians) * magnitude * (Math.sign(vy) || 1),
  }
}

const getLineResizeCursor = (annotation: KonvaAnnotationTemplate) => {
  if (!isLineBasedAnnotation(annotation)) return 'move'

  const angle =
    (((getLineVector(annotation.geometry.points).angleDeg +
      (annotation.geometry.rotation ?? 0)) %
      180) +
      180) %
    180

  if (angle < 22.5 || angle >= 157.5) return 'ew-resize'
  if (angle < 67.5) return 'nwse-resize'
  if (angle < 112.5) return 'ns-resize'
  return 'nesw-resize'
}

const getContextMenuPositionFromClientPoint = ({
  clientX,
  clientY,
  localMenuWidth,
  positioningElement,
  visibleMenuWidth,
}: {
  clientX: number
  clientY: number
  localMenuWidth: number
  positioningElement: HTMLElement | null
  visibleMenuWidth: number
}) => {
  if (!positioningElement) {
    return {
      left: clientX,
      top: clientY,
    }
  }

  const point = getLocalPointFromClientPoint({
    clientX,
    clientY,
    element: positioningElement,
  })
  const viewportWidth =
    globalThis.visualViewport?.width ??
    globalThis.innerWidth ??
    document.documentElement.clientWidth
  const shouldOpenLeft =
    viewportWidth > 0 && clientX + visibleMenuWidth > viewportWidth

  return {
    left: shouldOpenLeft ? Math.max(0, point.x - localMenuWidth) : point.x,
    top: point.y,
  }
}

const getKonvaAnnotationTextEditorState = ({
  annotation,
  subPageIds,
  template,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
  template: TemplateAttribute<any>
}): KonvaAnnotationTextEditorState => {
  if (
    annotation.kind !== 'arrow' ||
    annotation.geometry.mode !== 'points' ||
    !annotation.text?.enabled ||
    (annotation.text.placement !== 'middle' &&
      annotation.text.placement !== 'bottom')
  ) {
    return null
  }

  const textLayout = getArrowTextLayout(annotation)
  if (!textLayout) return null

  // Text layout is annotation-local; the input is portal-rendered in document
  // coordinates, so add the annotation origin and resolved subpage Y.
  const absoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })

  return {
    bounds: {
      height: textLayout.textHeight,
      left: annotation.geometry.x + textLayout.textX,
      top: absoluteY + textLayout.textY - 4,
      width: textLayout.textWidth,
    },
    maxLength: annotation.text.maxLength,
    template,
    text: annotation.text.value ?? '',
  }
}

const KonvaAnnotationTextEditor = ({
  editor,
  shouldFocus,
  onCommit,
  onFocused,
  onClose,
}: {
  editor: NonNullable<KonvaAnnotationTextEditorState>
  shouldFocus: boolean
  onCommit: (text: string) => void
  onFocused: () => void
  onClose: () => void
}) => {
  const [text, setText] = useState(editor.text)
  const commitRef = useRef(onCommit)
  const committedTextRef = useRef(editor.text)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const textRef = useRef(editor.text)

  const commitText = useCallback(() => {
    const nextText = textRef.current

    if (nextText === committedTextRef.current) {
      return
    }

    committedTextRef.current = nextText
    commitRef.current(nextText)
  }, [])

  useEffect(() => {
    commitRef.current = onCommit
  }, [onCommit])

  useEffect(() => {
    committedTextRef.current = editor.text
    textRef.current = editor.text
    setText(editor.text)

    return () => {
      commitText()
    }
  }, [commitText, editor.template.id, editor.text])

  useEffect(() => {
    if (!shouldFocus) return

    let didFocus = false

    const focusInput = () => {
      if (didFocus) return

      didFocus = true
      inputRef.current?.focus()
      inputRef.current?.select()
      onFocused()
    }

    const focusAfterClick = () => {
      window.setTimeout(focusInput, 0)
    }
    const fallbackFocusTimeout = window.setTimeout(focusInput, 150)

    window.addEventListener('click', focusAfterClick, true)

    return () => {
      window.clearTimeout(fallbackFocusTimeout)
      window.removeEventListener('click', focusAfterClick, true)
    }
  }, [editor.template.id, onFocused, shouldFocus])

  return (
    <Input
      ref={inputRef}
      data-konva-annotation-layer
      className="pointer-events-auto absolute z-[3] h-5 border-0 bg-white px-3 py-2 text-center text-xs shadow-none"
      maxLength={editor.maxLength}
      placeholder="Add text..."
      value={text}
      onChange={(event) => {
        textRef.current = event.target.value
        setText(event.target.value)
      }}
      onBlur={() => {
        commitText()
        onClose()
      }}
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
      onTouchStart={(event) => {
        event.stopPropagation()
      }}
      onClick={(event) => {
        event.stopPropagation()
      }}
      onKeyDown={(event) => {
        event.stopPropagation()

        if (event.key === 'Enter') {
          commitText()
          event.currentTarget.blur()
        }
      }}
      style={{
        height: editor.bounds.height,
        left: editor.bounds.left,
        top: editor.bounds.top,
        width: editor.bounds.width,
      }}
    />
  )
}

const KonvaLineResizeHandlesOverlay = ({
  annotation,
  position,
  template,
  templateId,
  onStart,
}: {
  annotation: KonvaAnnotationTemplate
  position: { x: number; y: number }
  template: TemplateAttribute<any>
  templateId: string
  onStart: (resize: ActiveLineResize) => void
}) => {
  if (!isLineBasedAnnotation(annotation)) return null

  const rotation = annotation.geometry.rotation ?? 0
  const cursor = getLineResizeCursor(annotation)

  return (
    <>
      {getLineResizeHandleGeometry(annotation).map((handle) => {
        const rotatedHandle = rotatePoint({
          angle: rotation,
          point: { x: handle.x, y: handle.y },
        })

        return (
          <div
            key={`line-resize-handle-${templateId}-${handle.index}`}
            data-konva-annotation-layer
            className="pointer-events-auto absolute z-[3] h-3 w-3 rounded-full border-2 border-blue-600 bg-white shadow-sm"
            style={{
              cursor,
              left: position.x + rotatedHandle.x,
              top: position.y + rotatedHandle.y,
              transform: 'translate(-50%, -50%)',
            }}
            onPointerDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
              event.currentTarget.setPointerCapture(event.pointerId)
              onStart({
                annotation,
                handle,
                position,
                template,
                templateId,
              })
            }}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
          />
        )
      })}
    </>
  )
}

const getViewportBounds = ({
  annotationDocumentBounds,
  containerWidth,
  useTightPadding = false,
}: {
  annotationDocumentBounds: DocumentAnnotationBounds[]
  containerWidth: number
  useTightPadding?: boolean
}) => {
  const [firstAnnotationDocumentBound, ...remainingAnnotationDocumentBounds] =
    annotationDocumentBounds
  const viewportBounds = remainingAnnotationDocumentBounds.reduce(
    (currentBounds, annotationDocumentBound) => ({
      left: Math.min(currentBounds.left, annotationDocumentBound.left),
      top: Math.min(currentBounds.top, annotationDocumentBound.top),
      right: Math.max(
        currentBounds.right,
        annotationDocumentBound.left + annotationDocumentBound.width,
      ),
      bottom: Math.max(
        currentBounds.bottom,
        annotationDocumentBound.top + annotationDocumentBound.height,
      ),
    }),
    {
      left: firstAnnotationDocumentBound.left,
      top: firstAnnotationDocumentBound.top,
      right:
        firstAnnotationDocumentBound.left + firstAnnotationDocumentBound.width,
      bottom:
        firstAnnotationDocumentBound.top + firstAnnotationDocumentBound.height,
    },
  )

  const horizontalPadding = useTightPadding
    ? IDLE_STAGE_PADDING
    : Math.max(containerWidth, MIN_HORIZONTAL_STAGE_PADDING)
  const verticalPadding = useTightPadding
    ? IDLE_STAGE_PADDING
    : Math.max(LETTER_PAGE_HEIGHT, MIN_VERTICAL_STAGE_PADDING)
  const left = Math.floor(viewportBounds.left - horizontalPadding)
  const top = Math.floor(viewportBounds.top - verticalPadding)
  const right = Math.ceil(viewportBounds.right + horizontalPadding)
  const bottom = Math.ceil(viewportBounds.bottom + verticalPadding)

  return {
    left,
    top,
    width: Math.max(right - left, 1),
    height: Math.max(bottom - top, 1),
  }
}

const getAnnotationStageClusters = (
  annotationItems: RenderableAnnotationItem[],
): AnnotationStageCluster[] =>
  annotationItems.map((item) => ({
    bounds: [item.bounds],
    id: item.templateId,
    items: [item],
    rect: item.bounds,
  }))

const getAnnotationProxyStyle = ({
  bounds,
  translateStyle,
}: {
  bounds: DocumentAnnotationBounds
  translateStyle?: string
}): React.CSSProperties => {
  return {
    height: Math.max(bounds.height, 1),
    left: bounds.left,
    top: bounds.top,
    translate: translateStyle,
    width: Math.max(bounds.width, 1),
  }
}

const getScreenKonvaPixelRatio = (zoomPercentage: number) => {
  const devicePixelRatio =
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1

  return Math.min(
    Math.max(devicePixelRatio, devicePixelRatio * zoomPercentage),
    MAX_SCREEN_KONVA_PIXEL_RATIO,
  )
}

const PrintAwareKonvaLayer = ({
  children,
  pixelRatio,
}: {
  children: ReactNode
  pixelRatio?: number
}) => {
  const layerRef = useRef<any>(null)

  useEffect(() => {
    if (!pixelRatio) return

    const layer = layerRef.current
    const canvas = layer?.getCanvas?.()

    if (!canvas || canvas.getPixelRatio?.() === pixelRatio) return

    canvas.setPixelRatio(pixelRatio)
    layer.batchDraw?.()
  }, [pixelRatio])

  return (
    <Layer ref={layerRef} listening={false}>
      {children}
    </Layer>
  )
}

PrintAwareKonvaLayer.displayName = 'PrintAwareKonvaLayer'

const getLineAnnotationProxyGeometry = ({
  annotation,
  subPageIds,
  translateStyle,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
  translateStyle?: string
}): {
  line: { x1: number; x2: number; y1: number; y2: number }
  style: React.CSSProperties
} | null => {
  if (!isLineBasedAnnotation(annotation)) {
    return null
  }

  const absoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })
  const rotation = annotation.geometry.rotation ?? 0
  const linePoints =
    annotation.kind === 'bubble'
      ? (getBubbleLayout(annotation)?.linePoints ?? annotation.geometry.points)
      : (getArrowTextLayout(annotation)?.arrowPoints ??
        annotation.geometry.points)
  const { x1, y1, x2, y2 } = getLineVector(linePoints)
  const start = rotatePoint({ angle: rotation, point: { x: x1, y: y1 } })
  const end = rotatePoint({ angle: rotation, point: { x: x2, y: y2 } })
  const startX = annotation.geometry.x + start.x
  const startY = absoluteY + start.y
  const endX = annotation.geometry.x + end.x
  const endY = absoluteY + end.y
  const padding = LINE_ANNOTATION_PROXY_HIT_WIDTH
  const left = Math.min(startX, endX) - padding / 2
  const top = Math.min(startY, endY) - padding / 2
  const width = Math.max(Math.abs(endX - startX) + padding, 1)
  const height = Math.max(Math.abs(endY - startY) + padding, 1)

  return {
    line: {
      x1: startX - left,
      x2: endX - left,
      y1: startY - top,
      y2: endY - top,
    },
    style: {
      height,
      left,
      pointerEvents: 'none',
      top,
      translate: translateStyle,
      width,
    },
  }
}

const getArrowTextProxyStyle = ({
  annotation,
  subPageIds,
  translateStyle,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
  translateStyle?: string
}): React.CSSProperties | null => {
  const textLayout = getArrowTextLayout(annotation)
  if (!textLayout) return null

  const absoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })
  const rotation = annotation.geometry.rotation ?? 0
  const topLeft = rotatePoint({
    angle: rotation,
    point: {
      x: textLayout.textX,
      y: textLayout.textY - 4,
    },
  })

  return {
    height: textLayout.textHeight,
    left: annotation.geometry.x + topLeft.x,
    top: absoluteY + topLeft.y,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: 'top left',
    translate: translateStyle,
    width: textLayout.textWidth,
  }
}

const getBubbleCircleProxyGeometry = ({
  annotation,
  subPageIds,
  translateStyle,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
  translateStyle?: string
}): {
  circle: { cx: number; cy: number; r: number }
  style: React.CSSProperties
} | null => {
  const bubbleLayout = getBubbleLayout(annotation)
  if (!bubbleLayout) return null

  const absoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })
  const rotation = annotation.geometry.rotation ?? 0
  const center = rotatePoint({
    angle: rotation,
    point: bubbleLayout.bubbleCenter,
  })
  const diameter = bubbleLayout.bubbleRadius * 2
  const left = annotation.geometry.x + center.x - bubbleLayout.bubbleRadius
  const top = absoluteY + center.y - bubbleLayout.bubbleRadius

  return {
    circle: {
      cx: bubbleLayout.bubbleRadius,
      cy: bubbleLayout.bubbleRadius,
      r: bubbleLayout.bubbleRadius,
    },
    style: {
      height: diameter,
      left,
      pointerEvents: 'none',
      top,
      translate: translateStyle,
      width: diameter,
    },
  }
}

const KonvaAnnotationLayerItem = memo(
  ({
    annotation,
    documentPage,
    dragOffset,
    isInteractive,
    isMultiSelected,
    isSelected,
    lineResizePoints,
    renderArrowText = true,
    template,
    onAnnotationDragEnd,
    onAnnotationDragMove,
    onAnnotationDragStart,
    onAnnotationSelect,
    onContextMenu,
    onMultiSelectPointerDown,
    onMultiSelectPointerUp,
    onNodeRef,
    onTooltipHide,
    onTooltipShow,
  }: KonvaAnnotationLayerItemProps) => {
    const templateId = template.id as string

    const handleSelect = useCallback(() => {
      onAnnotationSelect(template)
    }, [onAnnotationSelect, template])

    const handlePointerDown = useCallback(
      (event: MouseEvent | TouchEvent) => {
        onMultiSelectPointerDown(event, template)
      },
      [onMultiSelectPointerDown, template],
    )

    const handleDragStart = useCallback(() => {
      onTooltipHide(templateId)
      onAnnotationDragStart(templateId)
    }, [onAnnotationDragStart, onTooltipHide, templateId])

    const handleDragMove = useCallback(
      (position: { x: number; y: number }) => {
        onAnnotationDragMove({
          annotation,
          position,
          templateId,
        })
      },
      [annotation, onAnnotationDragMove, templateId],
    )

    const handleDragEnd = useCallback(
      (position: { x: number; y: number }) => {
        void onAnnotationDragEnd({
          annotation,
          position,
          template,
        })
      },
      [annotation, onAnnotationDragEnd, template],
    )

    return (
      <KonvaAnnotationNode
        template={template}
        documentPage={documentPage}
        annotation={annotation}
        draggable={isInteractive}
        dragOffset={dragOffset}
        isSelected={isSelected || isMultiSelected}
        lineResizePoints={lineResizePoints}
        renderArrowText={renderArrowText}
        onSelect={isInteractive ? handleSelect : undefined}
        onPointerDown={isInteractive ? handlePointerDown : undefined}
        onPointerUp={isInteractive ? onMultiSelectPointerUp : undefined}
        onMouseEnter={
          isInteractive
            ? () => {
                onTooltipShow({ annotation, templateId })
              }
            : undefined
        }
        onMouseLeave={
          isInteractive
            ? () => {
                onTooltipHide(templateId)
              }
            : undefined
        }
        onDragStart={isInteractive ? handleDragStart : undefined}
        onDragMove={isInteractive ? handleDragMove : undefined}
        onDragEnd={isInteractive ? handleDragEnd : undefined}
        onContextMenu={
          isInteractive
            ? (event) => {
                onContextMenu({ event, template })
              }
            : undefined
        }
        onNodeRef={onNodeRef}
      />
    )
  },
)
KonvaAnnotationLayerItem.displayName = 'KonvaAnnotationLayerItem'

const KonvaAnnotationInteractionProxy = memo(
  ({
    annotation,
    bounds,
    isInteractive,
    showArrowText,
    subPageIds,
    template,
    templateId,
    onAnnotationSelect,
    onAnnotationTextEditRequest,
    onContextMenu,
    onMultiSelectPointerDown,
    onMultiSelectPointerUp,
    onTooltipHide,
    onTooltipShow,
  }: KonvaAnnotationInteractionProxyProps) => {
    const { attributes, listeners, setNodeRef, transform } =
      useMultiSelectDraggable({
        id: templateId,
        data: { type: 'annotation' },
        disabled: !isInteractive,
      })

    const handlePointerDown = useCallback(
      (event: React.PointerEvent<Element>) => {
        if (!isInteractive) return

        onMultiSelectPointerDown(event, template)
      },
      [isInteractive, onMultiSelectPointerDown, template],
    )

    const handlePointerUp = useCallback(
      (event: React.PointerEvent<Element>) => {
        if (!isInteractive) return

        onMultiSelectPointerUp(event)
      },
      [isInteractive, onMultiSelectPointerUp],
    )

    const handleMouseDown = useCallback(
      (event: React.MouseEvent<Element>) => {
        if (!isInteractive) return

        listeners?.onMouseDown?.(event)

        if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
          onAnnotationSelect(template)
        }
      },
      [isInteractive, listeners, onAnnotationSelect, template],
    )

    const handleArrowTextPointerDown = useCallback(
      (event: React.PointerEvent<Element>) => {
        if (!isInteractive) return

        event.preventDefault()
        event.stopPropagation()

        if (event.shiftKey || event.ctrlKey || event.metaKey) {
          onMultiSelectPointerDown(event, template)
          return
        }

        onAnnotationSelect(template)
        onAnnotationTextEditRequest(templateId)
      },
      [
        isInteractive,
        onMultiSelectPointerDown,
        onAnnotationSelect,
        onAnnotationTextEditRequest,
        template,
        templateId,
      ],
    )

    const stopArrowTextPointerUp = useCallback(
      (event: React.PointerEvent<Element>) => {
        if (!isInteractive) return

        event.preventDefault()
        event.stopPropagation()
      },
      [isInteractive],
    )

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<Element>) => {
        if (!isInteractive) return

        event.preventDefault()
        event.stopPropagation()
        onContextMenu({
          clientX: event.clientX,
          clientY: event.clientY,
          template,
        })
      },
      [isInteractive, onContextMenu, template],
    )

    const translateStyle =
      transform && (transform.x || transform.y)
        ? `${transform.x ?? 0}px ${transform.y ?? 0}px`
        : undefined
    const lineProxyGeometry = getLineAnnotationProxyGeometry({
      annotation,
      subPageIds,
      translateStyle,
    })
    const proxyZIndex = getAnnotationInteractionZ(annotation)

    if (lineProxyGeometry) {
      const arrowTextProxyStyle = getArrowTextProxyStyle({
        annotation,
        subPageIds,
        translateStyle,
      })
      const bubbleCircleProxyGeometry = getBubbleCircleProxyGeometry({
        annotation,
        subPageIds,
        translateStyle,
      })

      return (
        <>
          <svg
            ref={(node) => {
              setNodeRef(node as unknown as HTMLElement | null)
            }}
            data-konva-annotation-layer
            focusable="false"
            tabIndex={-1}
            className="pointer-events-none absolute overflow-visible"
            style={{
              ...lineProxyGeometry.style,
              outline: 'none',
              zIndex: proxyZIndex,
            }}
          >
            <line
              data-konva-annotation-layer
              {...attributes}
              {...listeners}
              tabIndex={-1}
              x1={lineProxyGeometry.line.x1}
              x2={lineProxyGeometry.line.x2}
              y1={lineProxyGeometry.line.y1}
              y2={lineProxyGeometry.line.y2}
              fill="none"
              stroke="transparent"
              strokeLinecap="round"
              strokeWidth={LINE_ANNOTATION_PROXY_HIT_WIDTH}
              className={cn('touch-none', {
                'cursor-grab active:cursor-grabbing': isInteractive,
              })}
              pointerEvents={isInteractive ? 'stroke' : 'none'}
              style={{ outline: 'none' }}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onMouseDown={handleMouseDown}
              onContextMenu={handleContextMenu}
              onDoubleClick={(event) => {
                if (!isInteractive) return

                event.preventDefault()
                event.stopPropagation()
                onAnnotationTextEditRequest(templateId)
              }}
              onMouseEnter={() => {
                onTooltipShow({ annotation, templateId })
              }}
              onMouseLeave={() => {
                onTooltipHide(templateId)
              }}
            />
          </svg>
          {bubbleCircleProxyGeometry && (
            <svg
              data-konva-annotation-layer
              focusable="false"
              tabIndex={-1}
              className="pointer-events-none absolute overflow-visible"
              style={{
                ...bubbleCircleProxyGeometry.style,
                outline: 'none',
                zIndex: proxyZIndex,
              }}
            >
              <circle
                data-konva-annotation-layer
                {...attributes}
                {...listeners}
                tabIndex={-1}
                cx={bubbleCircleProxyGeometry.circle.cx}
                cy={bubbleCircleProxyGeometry.circle.cy}
                r={bubbleCircleProxyGeometry.circle.r}
                fill="transparent"
                className={cn('touch-none', {
                  'cursor-grab active:cursor-grabbing': isInteractive,
                })}
                pointerEvents={isInteractive ? 'all' : 'none'}
                style={{ outline: 'none' }}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onMouseDown={handleMouseDown}
                onContextMenu={handleContextMenu}
                onDoubleClick={(event) => {
                  if (!isInteractive) return

                  event.preventDefault()
                  event.stopPropagation()
                  onAnnotationTextEditRequest(templateId)
                }}
                onMouseEnter={() => {
                  onTooltipShow({ annotation, templateId })
                }}
                onMouseLeave={() => {
                  onTooltipHide(templateId)
                }}
              />
            </svg>
          )}
          {arrowTextProxyStyle && (
            <Input
              data-konva-annotation-layer
              {...attributes}
              {...listeners}
              readOnly
              tabIndex={-1}
              className={cn(
                'pointer-events-auto absolute touch-none cursor-text border-0 px-3 py-2 text-center text-xs shadow-none focus-visible:ring-0',
                showArrowText
                  ? 'bg-white text-black placeholder:text-slate-500'
                  : 'bg-transparent text-transparent opacity-0 placeholder:text-transparent',
                {
                  'pointer-events-none': !isInteractive,
                },
              )}
              maxLength={annotation.text?.maxLength}
              placeholder="Add text..."
              style={{
                ...arrowTextProxyStyle,
                outline: 'none',
                zIndex: proxyZIndex,
              }}
              value={annotation.text?.value ?? ''}
              onPointerDown={handleArrowTextPointerDown}
              onPointerUp={stopArrowTextPointerUp}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
              }}
              onContextMenu={handleContextMenu}
              onDoubleClick={(event) => {
                if (!isInteractive) return

                event.preventDefault()
                event.stopPropagation()
                onAnnotationTextEditRequest(templateId)
              }}
              onMouseEnter={() => {
                onTooltipShow({ annotation, templateId })
              }}
              onMouseLeave={() => {
                onTooltipHide(templateId)
              }}
            />
          )}
        </>
      )
    }

    const proxyStyle = getAnnotationProxyStyle({
      bounds,
      translateStyle,
    })

    return (
      <div
        ref={setNodeRef}
        data-konva-annotation-layer
        {...attributes}
        {...listeners}
        tabIndex={-1}
        className={cn(
          'pointer-events-auto absolute touch-none bg-transparent',
          {
            'cursor-grab active:cursor-grabbing': isInteractive,
            'pointer-events-none': !isInteractive,
          },
        )}
        style={{ ...proxyStyle, outline: 'none', zIndex: proxyZIndex }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
        onDoubleClick={(event) => {
          if (!isInteractive) return

          event.preventDefault()
          event.stopPropagation()
          onAnnotationTextEditRequest(templateId)
        }}
        onMouseEnter={() => {
          onTooltipShow({ annotation, templateId })
        }}
        onMouseLeave={() => {
          onTooltipHide(templateId)
        }}
      />
    )
  },
)
KonvaAnnotationInteractionProxy.displayName = 'KonvaAnnotationInteractionProxy'

export const KonvaAnnotationsLayer = ({
  annotationLayerActions,
  containerRef,
  documentPage,
  floatingOverlayElement,
  isInteractive,
  multiSelectedElementIds = [],
  selectedElementId,
  stackZIndex = 1,
  templateAttributes,
  onAnnotationDragStateChange,
  onAnnotationDragEnd,
  onAnnotationResizeEnd,
  onAnnotationColorChange,
  onAnnotationTextChange,
  onAnnotationSelect,
  onClearSelection,
}: KonvaAnnotationsLayerProps) => {
  const [isPrintPage] = useRoute(/print$/)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [activeDragDelta, setActiveDragDelta] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [lineResizeDraft, setLineResizeDraft] = useState<LineResizeDraft>(null)
  const [activeTextEditTemplateId, setActiveTextEditTemplateId] = useState<
    string | null
  >(null)
  const [textEditFocusTemplateId, setTextEditFocusTemplateId] = useState<
    string | null
  >(null)
  const [tooltip, setTooltip] = useState<KonvaAnnotationTooltipState>(null)
  const [contextMenu, setContextMenu] =
    useState<KonvaAnnotationContextMenuState>(null)
  const [settlingDragId, setSettlingDragId] = useState<string | null>(null)
  const [settlingDragDelta, setSettlingDragDelta] = useState({ x: 0, y: 0 })
  const [keyboardNudgeSettling, setKeyboardNudgeSettling] =
    useState<KeyboardNudgeSettlingState>(null)
  const [keyboardNudgePreview, setKeyboardNudgePreview] =
    useState<KeyboardNudgePreviewState | null>(null)
  const {
    onPointerDown: onMultiSelectPointerDown,
    onPointerUp: onMultiSelectPointerUp,
  } = useMultiSelectHandler()
  const zoom = useDocumentState((state) => state.zoom)
  const draggingElementId = useDocumentState((state) => state.draggingElementId)
  const setMultiSelectDelta = useDocumentState(
    (state) => state.setMultiSelectDelta,
  )
  const zoomPercentage = zoom / 100
  const contextMenuScale = zoomPercentage > 0 ? 1 / zoomPercentage : 1
  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = annotationLayerActions

  const dragDeltaFrameRef = useRef<number | null>(null)
  const pendingDragDeltaRef = useRef<{
    templateId: string
    delta: { x: number; y: number }
  } | null>(null)
  const activeDragStartPositionsRef = useRef<
    Map<string, { x: number; y: number }>
  >(new Map())
  const settlingDragStartPositionsRef = useRef<
    Map<string, { x: number; y: number }>
  >(new Map())
  const tooltipShowTimeoutRef = useRef<number | null>(null)
  const contextMenuRef = useRef<HTMLDivElement | null>(null)
  const activeLineResizeRef = useRef<ActiveLineResize | null>(null)
  const annotationNodeRefs = useRef<Map<string, any>>(new Map())
  const lastImperativeDragPreviewIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    return () => {
      if (dragDeltaFrameRef.current !== null) {
        cancelAnimationFrame(dragDeltaFrameRef.current)
        dragDeltaFrameRef.current = null
      }

      pendingDragDeltaRef.current = null
      settlingDragStartPositionsRef.current.clear()
      if (tooltipShowTimeoutRef.current !== null) {
        clearTimeout(tooltipShowTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!contextMenu) return

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        contextMenuRef.current?.contains(event.target)
      ) {
        return
      }

      setContextMenu(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContextMenu(null)
      }
    }

    window.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [contextMenu])

  const requestAnnotationTextEdit = useCallback((templateId: string) => {
    setActiveTextEditTemplateId(templateId)
    setTextEditFocusTemplateId(templateId)
  }, [])

  const flushPendingDragDelta = useCallback(() => {
    dragDeltaFrameRef.current = null

    const pending = pendingDragDeltaRef.current
    if (!pending) return

    setActiveDragDelta(pending.delta)
    setMultiSelectDelta({
      x: pending.delta.x * zoomPercentage,
      y: pending.delta.y * zoomPercentage,
    })
  }, [setMultiSelectDelta, zoomPercentage])

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const updateSize = () => {
      setSize({
        width: element.clientWidth,
        height: element.clientHeight,
      })
    }

    updateSize()

    const observer = new ResizeObserver(() => {
      updateSize()
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [containerRef])

  const subPageIds = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id as string,
      ],
    [documentPage],
  )

  const getAnnotationAbsolutePosition = useCallback(
    (annotation: KonvaAnnotationTemplate) =>
      ({
        x: annotation.geometry.x,
        y: getAbsoluteYFromSubPage({
          subPageId: annotation.subPageId ?? subPageIds[0],
          relativeY: annotation.geometry.y,
          subPageIds,
        }),
      }) as const,
    [subPageIds],
  )

  const annotationItems = useMemo<RenderableAnnotationItem[]>(
    () =>
      templateAttributes
        .map((templateAttribute, index) => {
          if (templateAttribute.data_type !== 'annotation') {
            return null
          }

          const annotation = toRenderableKonvaAnnotation(
            templateAttribute.template_values,
          )

          return {
            annotation,
            bounds: getDocumentAnnotationBounds({ annotation, subPageIds }),
            index,
            position: getAnnotationAbsolutePosition(annotation),
            template: templateAttribute,
            templateId: templateAttribute.id as string,
          }
        })
        .filter((item): item is RenderableAnnotationItem & { index: number } =>
          Boolean(item),
        )
        // react-konva library derives zIndex from order of elements
        .sort((a, b) => {
          const zA = a.annotation.geometry.z ?? 1000
          const zB = b.annotation.geometry.z ?? 1000

          return zA === zB ? a.index - b.index : zA - zB
        })
        .map(({ annotation, bounds, position, template, templateId }) => ({
          annotation,
          bounds,
          position,
          template,
          templateId,
        })),
    [getAnnotationAbsolutePosition, subPageIds, templateAttributes],
  )

  const multiSelectedElementIdSet = useMemo(
    () => new Set(multiSelectedElementIds),
    [multiSelectedElementIds],
  )

  const registerAnnotationNode = useCallback(
    (templateId: string, node: any | null) => {
      if (node) {
        annotationNodeRefs.current.set(templateId, node)
        return
      }

      annotationNodeRefs.current.delete(templateId)
    },
    [],
  )

  const resetImperativeDragPreview = useCallback(
    (templateIds = Array.from(lastImperativeDragPreviewIdsRef.current)) => {
      const affectedLayers = new Set<any>()

      templateIds.forEach((templateId) => {
        const node = annotationNodeRefs.current.get(templateId)
        const item = annotationItems.find(
          (annotationItem) => annotationItem.templateId === templateId,
        )

        if (!node || !item) return

        node.position(item.position)
        const layer = node.getLayer?.()
        if (layer) {
          affectedLayers.add(layer)
        }
      })

      affectedLayers.forEach((layer) => layer.batchDraw?.())
      lastImperativeDragPreviewIdsRef.current.clear()
    },
    [annotationItems],
  )

  const getLegacyDragPreviewItems = useCallback(
    ({
      bulkSelectedTemplateAttributes,
      draggingId,
    }: {
      bulkSelectedTemplateAttributes: TemplateAttribute<any>[]
      draggingId: string | null
    }) => {
      if (!draggingId) return []

      const selectedIds = new Set(
        bulkSelectedTemplateAttributes.map(
          (templateAttribute) => templateAttribute.id as string,
        ),
      )

      return annotationItems.filter(
        (item) =>
          item.templateId === draggingId || selectedIds.has(item.templateId),
      )
    },
    [annotationItems],
  )

  const applyImperativeDragPreview = useCallback(
    ({
      bulkSelectedTemplateAttributes,
      delta,
      draggingId,
      zoomPercentage,
    }: {
      bulkSelectedTemplateAttributes: TemplateAttribute<any>[]
      delta: { x: number; y: number }
      draggingId: string | null
      zoomPercentage: number
    }) => {
      const previewItems = getLegacyDragPreviewItems({
        bulkSelectedTemplateAttributes,
        draggingId,
      })
      const nextPreviewIds = new Set(
        previewItems.map((item) => item.templateId),
      )
      const stalePreviewIds = Array.from(
        lastImperativeDragPreviewIdsRef.current,
      ).filter((templateId) => !nextPreviewIds.has(templateId))

      if (stalePreviewIds.length) {
        resetImperativeDragPreview(stalePreviewIds)
      }

      if (!previewItems.length) {
        lastImperativeDragPreviewIdsRef.current = nextPreviewIds
        return
      }

      const documentDelta = {
        x: delta.x / zoomPercentage,
        y: delta.y / zoomPercentage,
      }
      const affectedLayers = new Set<any>()

      previewItems.forEach((item) => {
        const node = annotationNodeRefs.current.get(item.templateId)
        if (!node) return

        node.position({
          x: item.position.x + documentDelta.x,
          y: item.position.y + documentDelta.y,
        })
        const layer = node.getLayer?.()
        if (layer) {
          affectedLayers.add(layer)
        }
      })

      affectedLayers.forEach((layer) => layer.batchDraw?.())
      lastImperativeDragPreviewIdsRef.current = nextPreviewIds
    },
    [getLegacyDragPreviewItems, resetImperativeDragPreview],
  )

  const clearPendingTooltip = useCallback(() => {
    if (tooltipShowTimeoutRef.current !== null) {
      clearTimeout(tooltipShowTimeoutRef.current)
      tooltipShowTimeoutRef.current = null
    }
  }, [])

  const handleTooltipShow = useCallback(
    ({
      annotation,
      templateId,
    }: {
      annotation: KonvaAnnotationTemplate
      templateId: string
    }) => {
      if (!isInteractive || isDragging || draggingElementId) return

      const label = getHardwareSpecificAnnotationLabel(annotation)
      if (!label) return

      clearPendingTooltip()
      tooltipShowTimeoutRef.current = window.setTimeout(() => {
        tooltipShowTimeoutRef.current = null
        setTooltip({
          bounds: getDocumentAnnotationBounds({ annotation, subPageIds }),
          label,
          templateId,
        })
      }, TOOLTIP_SHOW_DELAY_MS)
    },
    [
      clearPendingTooltip,
      draggingElementId,
      isDragging,
      isInteractive,
      subPageIds,
    ],
  )

  const handleTooltipHide = useCallback(
    (templateId: string) => {
      clearPendingTooltip()
      setTooltip((currentTooltip) =>
        currentTooltip?.templateId === templateId ? null : currentTooltip,
      )
    },
    [clearPendingTooltip],
  )

  const openAnnotationContextMenu = useCallback(
    ({
      clientX,
      clientY,
      template,
    }: {
      clientX: number
      clientY: number
      template: TemplateAttribute<any>
    }) => {
      if (!isInteractive) return

      clearPendingTooltip()
      setTooltip(null)
      onAnnotationSelect(template)

      const positioningElement = floatingOverlayElement ?? containerRef.current
      const localMenuWidth = CONTEXT_MENU_WIDTH * contextMenuScale
      const menuPosition = getContextMenuPositionFromClientPoint({
        clientX,
        clientY,
        localMenuWidth,
        positioningElement,
        visibleMenuWidth: CONTEXT_MENU_WIDTH,
      })

      setContextMenu({
        left: menuPosition.left,
        template,
        top: menuPosition.top,
      })
    },
    [
      clearPendingTooltip,
      containerRef,
      floatingOverlayElement,
      isInteractive,
      contextMenuScale,
      onAnnotationSelect,
    ],
  )

  const handleAnnotationContextMenu = useCallback(
    ({
      event,
      template,
    }: {
      event: KonvaEventObject<MouseEvent>
      template: TemplateAttribute<any>
    }) => {
      openAnnotationContextMenu({
        clientX: event.evt.clientX,
        clientY: event.evt.clientY,
        template,
      })
    },
    [openAnnotationContextMenu],
  )

  const contextMenuOptions = useMemo(
    () =>
      contextMenu
        ? [
            {
              label: 'Bring to front',
              action: () => bringToFront(contextMenu.template.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(contextMenu.template.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(contextMenu.template.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(contextMenu.template.id as string),
            },
          ]
        : [],
    [bringForward, bringToFront, contextMenu, sendBackward, sendToBack],
  )

  const selectContextMenuOption = useCallback(
    (action: () => void) => {
      if (!isOptionsMenuLoading) {
        action()
      }

      setContextMenu(null)
    },
    [isOptionsMenuLoading],
  )

  useEffect(() => {
    return useDocumentState.subscribe((state, previousState) => {
      const draggingId = state.draggingElementId
      const previousDraggingId = previousState.draggingElementId
      const finalScreenDelta =
        Math.abs(previousState.multiSelectDelta.x) > 0 ||
        Math.abs(previousState.multiSelectDelta.y) > 0
          ? previousState.multiSelectDelta
          : state.multiSelectDelta

      if (draggingId) {
        applyImperativeDragPreview({
          bulkSelectedTemplateAttributes: state.bulkSelectedTemplateAttributes,
          delta: state.multiSelectDelta,
          draggingId,
          zoomPercentage,
        })
      }

      if (!draggingId && previousDraggingId) {
        const hasFinalDelta =
          Math.abs(finalScreenDelta.x) > 0 || Math.abs(finalScreenDelta.y) > 0
        const selectedAnnotationItems = hasFinalDelta
          ? getLegacyDragPreviewItems({
              bulkSelectedTemplateAttributes:
                previousState.bulkSelectedTemplateAttributes,
              draggingId: previousDraggingId,
            })
          : []

        if (selectedAnnotationItems.length) {
          settlingDragStartPositionsRef.current = new Map(
            selectedAnnotationItems.map((item) => [
              item.templateId,
              item.position,
            ]),
          )
          setSettlingDragId(previousDraggingId)
          setSettlingDragDelta({
            x: finalScreenDelta.x / zoomPercentage,
            y: finalScreenDelta.y / zoomPercentage,
          })
        } else {
          resetImperativeDragPreview()
        }

        lastImperativeDragPreviewIdsRef.current.clear()
      }
    })
  }, [
    applyImperativeDragPreview,
    getLegacyDragPreviewItems,
    resetImperativeDragPreview,
    zoomPercentage,
  ])

  const updateDragDelta = useCallback(
    ({
      annotation,
      position,
      templateId,
    }: {
      annotation: KonvaAnnotationTemplate
      position: { x: number; y: number }
      templateId: string
    }) => {
      const hasFollowerSelection = multiSelectedElementIds.some(
        (id) => id !== templateId,
      )

      // Single annotation drag does not need React/Zustand updates per frame.
      if (!hasFollowerSelection) return

      const originalPosition = getAnnotationAbsolutePosition(annotation)
      pendingDragDeltaRef.current = {
        templateId,
        delta: {
          x: position.x - originalPosition.x,
          y: position.y - originalPosition.y,
        },
      }

      if (dragDeltaFrameRef.current === null) {
        dragDeltaFrameRef.current = requestAnimationFrame(flushPendingDragDelta)
      }
    },
    [
      flushPendingDragDelta,
      getAnnotationAbsolutePosition,
      multiSelectedElementIds,
    ],
  )

  const clearDragDelta = useCallback(() => {
    if (dragDeltaFrameRef.current !== null) {
      cancelAnimationFrame(dragDeltaFrameRef.current)
      dragDeltaFrameRef.current = null
    }

    pendingDragDeltaRef.current = null
    activeDragStartPositionsRef.current.clear()
    setActiveDragId(null)
    setActiveDragDelta({ x: 0, y: 0 })
    setMultiSelectDelta({ x: 0, y: 0 })
  }, [setMultiSelectDelta])

  const clearSettlingDrag = useCallback(() => {
    settlingDragStartPositionsRef.current.clear()
    setSettlingDragId(null)
    setSettlingDragDelta({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    if (!draggingElementId || !settlingDragId) return

    clearSettlingDrag()
  }, [clearSettlingDrag, draggingElementId, settlingDragId])

  const handleAnnotationDragStart = useCallback(
    (templateId: string) => {
      clearPendingTooltip()
      setTooltip(null)
      setIsDragging(true)
      onAnnotationDragStateChange?.(true)
      clearSettlingDrag()
      const hasFollowerSelection = multiSelectedElementIds.some(
        (id) => id !== templateId,
      )

      if (hasFollowerSelection) {
        activeDragStartPositionsRef.current = new Map(
          annotationItems
            .filter((item) => multiSelectedElementIds.includes(item.templateId))
            .map((item) => [item.templateId, item.position]),
        )
        setActiveDragId(templateId)
      } else {
        activeDragStartPositionsRef.current.clear()
      }
    },
    [
      annotationItems,
      clearSettlingDrag,
      clearPendingTooltip,
      multiSelectedElementIds,
      onAnnotationDragStateChange,
    ],
  )

  const handleAnnotationDragEnd = useCallback(
    async ({
      annotation,
      position,
      template,
    }: {
      annotation: KonvaAnnotationTemplate
      position: { x: number; y: number }
      template: TemplateAttribute<any>
    }) => {
      const originalPosition = getAnnotationAbsolutePosition(annotation)
      const hasFollowerSelection = multiSelectedElementIds.some(
        (id) => id !== (template.id as string),
      )

      if (hasFollowerSelection) {
        const finalDragDelta = {
          x: position.x - originalPosition.x,
          y: position.y - originalPosition.y,
        }
        settlingDragStartPositionsRef.current = new Map(
          activeDragStartPositionsRef.current,
        )
        setSettlingDragId(template.id as string)
        setSettlingDragDelta(finalDragDelta)
        clearDragDelta()
      }

      try {
        await onAnnotationDragEnd({
          annotation,
          position,
          template,
        })
      } finally {
        setIsDragging(false)
        onAnnotationDragStateChange?.(false)
        if (!hasFollowerSelection) {
          clearDragDelta()
        }
      }
    },
    [
      clearDragDelta,
      getAnnotationAbsolutePosition,
      multiSelectedElementIds,
      onAnnotationDragEnd,
      onAnnotationDragStateChange,
    ],
  )

  const updateLineResizeDraftFromPointer = useCallback(
    (event: Pick<PointerEvent, 'clientX' | 'clientY' | 'shiftKey'>) => {
      const activeResize = activeLineResizeRef.current
      const positioningElement = floatingOverlayElement ?? containerRef.current

      if (
        !activeResize ||
        !positioningElement ||
        !isLineBasedAnnotation(activeResize.annotation)
      ) {
        return null
      }

      const point = getLocalPointFromClientPoint({
        clientX: event.clientX,
        clientY: event.clientY,
        element: positioningElement,
      })
      const rotation = activeResize.annotation.geometry.rotation ?? 0
      const unrotatedPoint = rotatePoint({
        angle: -rotation,
        point: {
          x: point.x - activeResize.position.x,
          y: point.y - activeResize.position.y,
        },
      })
      const currentPoints =
        lineResizeDraft?.templateId === activeResize.templateId
          ? lineResizeDraft.points
          : activeResize.annotation.geometry.points
      const nextPoints = [...currentPoints]
      const fixedPointIndex = activeResize.handle.index === 0 ? 1 : 0
      const fixedX = currentPoints[fixedPointIndex * 2] ?? 0
      const fixedY = currentPoints[fixedPointIndex * 2 + 1] ?? 0
      let nextX = unrotatedPoint.x - activeResize.handle.handleOffsetX
      let nextY = unrotatedPoint.y - activeResize.handle.handleOffsetY

      if (event.shiftKey) {
        const snapped = snapVectorToAngles(nextX - fixedX, nextY - fixedY)
        nextX = fixedX + snapped.x
        nextY = fixedY + snapped.y
      }

      nextPoints[activeResize.handle.index * 2] = nextX
      nextPoints[activeResize.handle.index * 2 + 1] = nextY
      setLineResizeDraft({
        points: nextPoints,
        templateId: activeResize.templateId,
      })

      return nextPoints
    },
    [containerRef, floatingOverlayElement, lineResizeDraft],
  )

  const endLineResize = useCallback(
    async (event: Pick<PointerEvent, 'clientX' | 'clientY' | 'shiftKey'>) => {
      const activeResize = activeLineResizeRef.current
      const nextPoints = updateLineResizeDraftFromPointer(event)
      activeLineResizeRef.current = null

      if (!activeResize || !isLineBasedAnnotation(activeResize.annotation)) {
        setLineResizeDraft(null)
        return
      }

      const finalPoints =
        nextPoints ??
        (lineResizeDraft?.templateId === activeResize.templateId
          ? lineResizeDraft.points
          : activeResize.annotation.geometry.points)
      const resizedAnnotation: KonvaAnnotationTemplate = {
        ...activeResize.annotation,
        geometry: {
          ...activeResize.annotation.geometry,
          points: finalPoints,
          width: Math.abs((finalPoints[2] ?? 0) - (finalPoints[0] ?? 0)),
          height: Math.abs((finalPoints[3] ?? 0) - (finalPoints[1] ?? 0)),
        },
      }

      try {
        await onAnnotationResizeEnd({
          annotation: resizedAnnotation,
          template: activeResize.template,
        })
      } catch (error) {
        console.error('Failed to resize Konva annotation line', error)
      } finally {
        setLineResizeDraft(null)
        setIsDragging(false)
        onAnnotationDragStateChange?.(false)
        clearDragDelta()
      }
    },
    [
      clearDragDelta,
      lineResizeDraft,
      onAnnotationDragStateChange,
      onAnnotationResizeEnd,
      updateLineResizeDraftFromPointer,
    ],
  )

  const startLineResize = useCallback(
    ({
      annotation,
      handle,
      position,
      template,
      templateId,
    }: ActiveLineResize) => {
      activeLineResizeRef.current = {
        annotation,
        handle,
        position,
        template,
        templateId,
      }
      clearPendingTooltip()
      setTooltip(null)
      setIsDragging(true)
      onAnnotationDragStateChange?.(true)
      clearSettlingDrag()
      setLineResizeDraft({
        points:
          annotation.geometry.mode === 'points'
            ? annotation.geometry.points
            : [],
        templateId,
      })
    },
    [clearPendingTooltip, clearSettlingDrag, onAnnotationDragStateChange],
  )

  useEffect(() => {
    if (!lineResizeDraft) return

    const handlePointerMove = (event: PointerEvent) => {
      event.preventDefault()
      updateLineResizeDraftFromPointer(event)
    }

    const handlePointerUp = (event: PointerEvent) => {
      event.preventDefault()
      void endLineResize(event)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [endLineResize, lineResizeDraft, updateLineResizeDraftFromPointer])

  useEffect(() => {
    if (!settlingDragId) return

    const settlingTemplateIds = Array.from(
      settlingDragStartPositionsRef.current.keys(),
    )

    if (!settlingTemplateIds.length) {
      clearSettlingDrag()
      return
    }

    const POSITION_EPSILON = 0.5
    const haveFollowersSettled = settlingTemplateIds.every((templateId) => {
      const dragStartPosition =
        settlingDragStartPositionsRef.current.get(templateId)
      if (!dragStartPosition) return true

      const currentItem = annotationItems.find(
        (item) => item.templateId === templateId,
      )
      if (!currentItem) return false

      const currentPosition = currentItem.position

      return (
        Math.abs(
          currentPosition.x - (dragStartPosition.x + settlingDragDelta.x),
        ) <= POSITION_EPSILON &&
        Math.abs(
          currentPosition.y - (dragStartPosition.y + settlingDragDelta.y),
        ) <= POSITION_EPSILON
      )
    })

    if (haveFollowersSettled) {
      clearSettlingDrag()
    }
  }, [annotationItems, clearSettlingDrag, settlingDragDelta, settlingDragId])

  useEffect(() => {
    if (!keyboardNudgeSettling) return

    const currentItem = annotationItems.find(
      (item) => item.templateId === keyboardNudgeSettling.templateId,
    )
    if (!currentItem) {
      setKeyboardNudgeSettling(null)
      return
    }

    const POSITION_EPSILON = 0.5
    const hasSettled =
      Math.abs(
        currentItem.position.x -
          (keyboardNudgeSettling.startPosition.x +
            keyboardNudgeSettling.delta.x),
      ) <= POSITION_EPSILON &&
      Math.abs(
        currentItem.position.y -
          (keyboardNudgeSettling.startPosition.y +
            keyboardNudgeSettling.delta.y),
      ) <= POSITION_EPSILON

    if (hasSettled) {
      setKeyboardNudgeSettling(null)
    }
  }, [annotationItems, keyboardNudgeSettling])

  const selectedAnnotationItem = annotationItems.find(
    (item) => item.templateId === selectedElementId,
  )
  const selectedTemplate = selectedAnnotationItem?.template ?? null
  const selectedAnnotation = selectedAnnotationItem?.annotation ?? null
  const getKeyboardNudgeOffset = ({
    currentPosition,
    templateId,
  }: {
    currentPosition: { x: number; y: number }
    templateId: string
  }) => {
    if (selectedElementId !== templateId) return null

    const nudgeState =
      keyboardNudgePreview?.templateId === templateId
        ? keyboardNudgePreview
        : keyboardNudgeSettling?.templateId === templateId
          ? keyboardNudgeSettling
          : null

    if (!nudgeState) return null

    return {
      x: nudgeState.delta.x - (currentPosition.x - nudgeState.startPosition.x),
      y: nudgeState.delta.y - (currentPosition.y - nudgeState.startPosition.y),
    }
  }
  const getSettlingDragOffset = ({
    currentPosition,
    templateId,
  }: {
    currentPosition: { x: number; y: number }
    templateId: string
  }) => {
    if (!settlingDragId || draggingElementId) return null

    const dragStartPosition =
      settlingDragStartPositionsRef.current.get(templateId)
    if (!dragStartPosition) return null

    return {
      x: settlingDragDelta.x - (currentPosition.x - dragStartPosition.x),
      y: settlingDragDelta.y - (currentPosition.y - dragStartPosition.y),
    }
  }
  const selectedKeyboardNudgeOffset = selectedAnnotationItem
    ? getKeyboardNudgeOffset({
        currentPosition: selectedAnnotationItem.position,
        templateId: selectedAnnotationItem.templateId,
      })
    : null
  const selectedTransientOffset = selectedAnnotationItem
    ? (getSettlingDragOffset({
        currentPosition: selectedAnnotationItem.position,
        templateId: selectedAnnotationItem.templateId,
      }) ?? selectedKeyboardNudgeOffset)
    : null
  const selectedLineResizeDraftPoints =
    lineResizeDraft &&
    selectedAnnotationItem &&
    lineResizeDraft.templateId === selectedElementId
      ? lineResizeDraft.points
      : null
  const selectedLineResizeAnnotation =
    selectedAnnotation && isLineBasedAnnotation(selectedAnnotation)
      ? ({
          ...selectedAnnotation,
          geometry: {
            ...selectedAnnotation.geometry,
            points:
              selectedLineResizeDraftPoints ??
              selectedAnnotation.geometry.points,
          },
        } satisfies KonvaAnnotationTemplate)
      : null
  const selectedLineResizeHandles =
    isInteractive &&
    selectedAnnotationItem &&
    selectedTemplate &&
    selectedLineResizeAnnotation &&
    multiSelectedElementIds.length === 0 &&
    !draggingElementId &&
    (!isDragging || lineResizeDraft?.templateId === selectedElementId)
      ? {
          annotation: selectedLineResizeAnnotation,
          position: {
            x:
              selectedAnnotationItem.position.x +
              (selectedTransientOffset?.x ?? 0),
            y:
              selectedAnnotationItem.position.y +
              (selectedTransientOffset?.y ?? 0),
          },
          template: selectedTemplate,
          templateId: selectedAnnotationItem.templateId,
        }
      : null
  const selectedAnnotationToolbar =
    selectedAnnotationItem &&
    selectedTemplate &&
    selectedAnnotation &&
    isColorEditableKonvaAnnotation(selectedAnnotation)
      ? {
          annotation: selectedAnnotation,
          bounds: {
            ...selectedAnnotationItem.bounds,
            left:
              selectedAnnotationItem.bounds.left +
              (selectedTransientOffset?.x ?? 0),
            top:
              selectedAnnotationItem.bounds.top +
              (selectedTransientOffset?.y ?? 0),
          },
          color: getSelectedAnnotationColor({
            annotation: selectedAnnotation,
            templateValues: selectedTemplate.template_values,
          }),
          template: selectedTemplate,
        }
      : null
  const selectedAnnotationTextEditor =
    isInteractive && activeTextEditTemplateId
      ? (() => {
          const textEditAnnotationItem = annotationItems.find(
            (item) => item.templateId === activeTextEditTemplateId,
          )
          const textEditTemplate = textEditAnnotationItem?.template ?? null
          const textEditAnnotation = textEditAnnotationItem?.annotation ?? null

          if (!textEditTemplate || !textEditAnnotation) return null

          const editor = getKonvaAnnotationTextEditorState({
            annotation: textEditAnnotation,
            subPageIds,
            template: textEditTemplate,
          })

          if (!editor) return null

          const textEditTransientOffset =
            selectedAnnotationItem?.templateId ===
            textEditAnnotationItem?.templateId
              ? selectedTransientOffset
              : null

          return {
            ...editor,
            bounds: {
              ...editor.bounds,
              left: editor.bounds.left + (textEditTransientOffset?.x ?? 0),
              top: editor.bounds.top + (textEditTransientOffset?.y ?? 0),
            },
          }
        })()
      : null
  useKeyboardNudge({
    bindToWindow: true,
    documentPage,
    isActive:
      isInteractive &&
      Boolean(selectedTemplate) &&
      !contextMenu &&
      !lineResizeDraft,
    onCommit: (delta) => {
      if (!selectedAnnotationItem) return

      setKeyboardNudgeSettling((currentSettling) => {
        const nextDelta = {
          x: delta.x / zoomPercentage,
          y: delta.y / zoomPercentage,
        }

        if (currentSettling?.templateId === selectedAnnotationItem.templateId) {
          return {
            ...currentSettling,
            delta: {
              x: currentSettling.delta.x + nextDelta.x,
              y: currentSettling.delta.y + nextDelta.y,
            },
          }
        }

        return {
          delta: nextDelta,
          startPosition: selectedAnnotationItem.position,
          templateId: selectedAnnotationItem.templateId,
        }
      })
    },
    onNudgeEnd: () => onAnnotationDragStateChange?.(false),
    onNudgeStart: () => onAnnotationDragStateChange?.(true),
    onPreviewChange: (delta) => {
      if (!selectedAnnotationItem) return

      const nextDelta = {
        x: delta.x / zoomPercentage,
        y: delta.y / zoomPercentage,
      }
      const hasDelta = Math.abs(nextDelta.x) > 0 || Math.abs(nextDelta.y) > 0

      setKeyboardNudgePreview((currentPreview) => {
        if (!hasDelta) {
          return null
        }

        if (currentPreview?.templateId === selectedAnnotationItem.templateId) {
          return {
            ...currentPreview,
            delta: nextDelta,
          }
        }

        return {
          delta: nextDelta,
          startPosition: selectedAnnotationItem.position,
          templateId: selectedAnnotationItem.templateId,
        }
      })
    },
    template: selectedTemplate,
  })

  const isPointerDragActive = isDragging || Boolean(draggingElementId)
  const useTightStagePadding =
    !isPointerDragActive &&
    !lineResizeDraft &&
    !settlingDragId &&
    !keyboardNudgePreview &&
    !keyboardNudgeSettling
  const stageClusters = useMemo(
    () => getAnnotationStageClusters(annotationItems),
    [annotationItems],
  )
  const printPixelRatio = isPrintPage
    ? PRINT_KONVA_PIXEL_RATIO
    : getScreenKonvaPixelRatio(zoomPercentage)

  if (!size.width || !size.height || !annotationItems.length) {
    return null
  }

  const floatingUi = (
    <>
      {isInteractive && !isPointerDragActive && tooltip && (
        <div
          className="pointer-events-none absolute z-[3] rounded bg-black px-2 py-1 text-xs text-white"
          style={{
            left: tooltip.bounds.left + tooltip.bounds.width / 2,
            top: tooltip.bounds.top - TOOLTIP_VERTICAL_OFFSET,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {tooltip.label}
        </div>
      )}
      {isInteractive && contextMenu && (
        <div
          ref={contextMenuRef}
          role="menu"
          className="pointer-events-auto absolute z-[4] w-48 rounded border bg-white p-1 shadow"
          style={{
            left: contextMenu.left,
            top: contextMenu.top,
            transform: `scale(${contextMenuScale})`,
            transformOrigin: 'top left',
          }}
          onContextMenu={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onMouseDown={(event) => {
            event.stopPropagation()
          }}
          onTouchStart={(event) => {
            event.stopPropagation()
          }}
        >
          <div className="mb-2 border-b last:mb-0 last:border-0">
            {contextMenuOptions.map(({ action, label }) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                className={cn(
                  'block w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-100',
                  {
                    'text-gray-400': isOptionsMenuLoading,
                    'cursor-pointer': !isOptionsMenuLoading,
                  },
                )}
                onClick={() => selectContextMenuOption(action)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {isInteractive && !isPointerDragActive && selectedAnnotationToolbar && (
        <KonvaAnnotationColorToolbar
          annotation={selectedAnnotationToolbar.annotation}
          bounds={selectedAnnotationToolbar.bounds}
          selectedColor={selectedAnnotationToolbar.color}
          onSelectColor={(color) => {
            void onAnnotationColorChange({
              annotation: selectedAnnotationToolbar.annotation,
              color,
              template: selectedAnnotationToolbar.template,
            })
          }}
        />
      )}
      {isInteractive &&
        !isPointerDragActive &&
        selectedAnnotationTextEditor && (
          <KonvaAnnotationTextEditor
            editor={selectedAnnotationTextEditor}
            shouldFocus={
              textEditFocusTemplateId ===
              (selectedAnnotationTextEditor.template.id as string)
            }
            onCommit={(text) => {
              void onAnnotationTextChange({
                text,
                template: selectedAnnotationTextEditor.template,
              })
            }}
            onFocused={() => setTextEditFocusTemplateId(null)}
            onClose={() => setActiveTextEditTemplateId(null)}
          />
        )}
      {selectedLineResizeHandles && (
        <KonvaLineResizeHandlesOverlay
          annotation={selectedLineResizeHandles.annotation}
          position={selectedLineResizeHandles.position}
          template={selectedLineResizeHandles.template}
          templateId={selectedLineResizeHandles.templateId}
          onStart={startLineResize}
        />
      )}
    </>
  )

  return (
    <div
      data-konva-annotation-layer
      className="pointer-events-none absolute inset-0 overflow-visible"
      style={{ contain: 'layout', zIndex: stackZIndex }}
      onMouseDown={(event) => {
        setContextMenu(null)

        if (shouldClearSelectionFromWrapper(event)) {
          onClearSelection()
        }
      }}
      onTouchStart={(event) => {
        setContextMenu(null)

        if (shouldClearSelectionFromWrapper(event)) {
          onClearSelection()
        }
      }}
    >
      {stageClusters.map((cluster) => {
        const clusterAnnotation = cluster.items[0]?.annotation
        if (!clusterAnnotation) return null

        const viewport = getViewportBounds({
          annotationDocumentBounds: cluster.bounds,
          containerWidth: size.width,
          useTightPadding: useTightStagePadding,
        })

        return (
          <Stage
            key={`annotation-stage-${cluster.id}`}
            width={viewport.width}
            height={viewport.height}
            style={{
              left: viewport.left,
              pointerEvents: 'none',
              position: 'absolute',
              top: viewport.top,
              zIndex: getAnnotationCanvasZ(clusterAnnotation),
            }}
          >
            <PrintAwareKonvaLayer pixelRatio={printPixelRatio}>
              <Group x={-viewport.left} y={-viewport.top}>
                {cluster.items.map((item) => {
                  const {
                    annotation,
                    position: currentPosition,
                    template,
                    templateId,
                  } = item
                  const isMultiSelected =
                    multiSelectedElementIdSet.has(templateId)
                  const isActiveDragTemplate = activeDragId === templateId
                  const dragOffset = (() => {
                    if (
                      isMultiSelected &&
                      activeDragId &&
                      !isActiveDragTemplate
                    ) {
                      const dragStartPosition =
                        activeDragStartPositionsRef.current.get(templateId)
                      if (!dragStartPosition) return activeDragDelta

                      return {
                        x:
                          activeDragDelta.x -
                          (currentPosition.x - dragStartPosition.x),
                        y:
                          activeDragDelta.y -
                          (currentPosition.y - dragStartPosition.y),
                      }
                    }

                    const settlingDragOffset = getSettlingDragOffset({
                      currentPosition,
                      templateId,
                    })
                    if (settlingDragOffset) {
                      return settlingDragOffset
                    }

                    const keyboardNudgeOffset = getKeyboardNudgeOffset({
                      currentPosition,
                      templateId,
                    })
                    if (keyboardNudgeOffset) {
                      return keyboardNudgeOffset
                    }

                    return undefined
                  })()

                  return (
                    <KonvaAnnotationLayerItem
                      key={templateId}
                      annotation={annotation}
                      template={template}
                      documentPage={documentPage}
                      isInteractive={false}
                      dragOffset={dragOffset}
                      isSelected={selectedElementId === templateId}
                      isMultiSelected={isMultiSelected}
                      lineResizePoints={
                        lineResizeDraft?.templateId === templateId
                          ? (selectedLineResizeDraftPoints ??
                            lineResizeDraft.points)
                          : null
                      }
                      renderArrowText={isPrintPage}
                      onAnnotationSelect={onAnnotationSelect}
                      onMultiSelectPointerDown={onMultiSelectPointerDown}
                      onMultiSelectPointerUp={onMultiSelectPointerUp}
                      onAnnotationDragStart={handleAnnotationDragStart}
                      onAnnotationDragMove={updateDragDelta}
                      onAnnotationDragEnd={handleAnnotationDragEnd}
                      onContextMenu={handleAnnotationContextMenu}
                      onTooltipShow={handleTooltipShow}
                      onTooltipHide={handleTooltipHide}
                      onNodeRef={registerAnnotationNode}
                    />
                  )
                })}
              </Group>
            </PrintAwareKonvaLayer>
          </Stage>
        )
      })}
      {annotationItems.map((item) => (
        <KonvaAnnotationInteractionProxy
          key={`interaction-${item.templateId}`}
          annotation={item.annotation}
          bounds={item.bounds}
          isInteractive={isInteractive}
          showArrowText={!isPrintPage}
          subPageIds={subPageIds}
          template={item.template}
          templateId={item.templateId}
          onAnnotationSelect={onAnnotationSelect}
          onAnnotationTextEditRequest={requestAnnotationTextEdit}
          onContextMenu={openAnnotationContextMenu}
          onMultiSelectPointerDown={onMultiSelectPointerDown}
          onMultiSelectPointerUp={onMultiSelectPointerUp}
          onTooltipShow={handleTooltipShow}
          onTooltipHide={handleTooltipHide}
        />
      ))}
      {annotationItems.map((item) => (
        <KonvaAnnotationInteractionBoundsMirror
          key={`bounds-${item.templateId}`}
          bounds={item.bounds}
          templateId={item.templateId}
        />
      ))}
      {floatingOverlayElement
        ? createPortal(floatingUi, floatingOverlayElement)
        : floatingUi}
    </div>
  )
}

export default KonvaAnnotationsLayer
